import { Inject, Injectable } from '@angular/core';
import {
	API_BASE_URL_BACKEND,
	DcBaseInitializationService,
	DcCoreBackendBasePath,
	toImmutableMap,
} from '@datachain/ui-sdk/common';
import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import {
	AppTagItemEntity,
	ColumnType,
} from '@datachain/ui-sdk/components/dc-types';
import { Map as ImMap, OrderedMap, OrderedSet, Set as ImSet } from 'immutable';

import { TagAdapter } from '../core/adapters/tag.adapter';
import dayjs from '../core/configs/dayjs';
import { ElementPermission } from '../core/domain/permission-types';
import { ProjectStorage } from '../core/services/project.storage';
import { DcRouteUrlBuilder } from '../core/utils/dc-route-url-builder';
import { normalize } from '../core/utils/normalize';
import { IProjectModel } from '../requesters';
import { IDcMetadataResponse } from '../requesters/core/models/metadata.model';
import { IShareableDatablockColumn } from '../requesters/exposition/models/shareable-datablock-column.model';
import { IShareableDatablock } from '../requesters/exposition/models/shareable-datablock.model';
import { ISubscriptionMemberAccess } from '../requesters/subscription/models/permission.model';
import { IPublicMetadata } from '../requesters/subscription/models/public-metadata.model';
import { ParsedSimulationResult } from '../requesters/subscription/models/simulator.model';
import {
	ISubscriptionColumnConfig,
	SubscriptionColumnUsage,
} from '../requesters/subscription/models/subscription-column-config.model';
import {
	HistoryAction,
	ISubscriptionHistory,
} from '../requesters/subscription/models/subscription-history.model';
import {
	IColumnTemplate,
	IFilterTemplate,
	ISubscriptionProfile,
} from '../requesters/subscription/models/subscription-profile.model';
import {
	IConsumedProfileColumn,
	ISubscriptionProjectModel,
	ProjectSubscriptionStatus,
} from '../requesters/subscription/models/subscription-project.model';
import {
	HistoryStatusModel,
	ISubscriptionResponse,
	SubscriptionColumnStatus,
} from '../requesters/subscription/models/subscription.model';
import {
	BlockedReason,
	ISwitchableProjectModel,
} from '../requesters/subscription/models/version-switch.model';
import { SubscriptionDatablockItemEntity } from './components/subscription-configuration-stepper/subscription-datablock-item.entity';
import { SubscriptionProjectItemEntity } from './components/subscription-configuration-stepper/subscription-project-item.entity';
import { SubscriptionItemEntity } from './components/subscription-list/subscription-item.entity';
import { SimulationResultEntity } from './components/subscription-simulator/simulation-result.entity';
import { ConsumedProjectItemEntity } from './components/subscription-view/consumed-project-item.entity';
import { ProjectItemEntity } from './components/subscription-view/consumer-config-modal/project-item.entity';
import { HistoryItemEntity } from './components/subscription-view/history-list-modal/history-item.entity';
import { MemberAccessEntity } from './components/subscription-view/permissions-config-modal/member-access.entity';
import {
	PublishedSubscriptionEntity,
	SubscriptionHistory,
} from './components/subscription-view/published-subscription.entity';
import { ViewDetailsEntity } from './components/subscription-view/view-details.entity';
import {
	SwitchableVersionProjectItemEntity,
	VersionSwitchBlockedReason,
} from './components/version-switcher-modal/switchable-version-project-item.entity';
import {
	ColumnConfigItemEntity,
	ColumnReferences,
} from './domain/column-config-item.entity';
import { ColumnModelItemEntity } from './domain/column-model-item.entity';
import { FilterModelItemEntity } from './domain/filter-model-item.entity';
import { InternalMetadataEntity } from './domain/internal-metadata.entity';
import { ProfileItemEntity } from './domain/profile-item.entity';
import { PublicMetadataEntity } from './domain/public-metadata.entity';
import {
	ColumnStatus,
	ConsumeStatus,
	DatablockColumn,
	SubscriptionStatus,
} from './domain/subscription.types';
import { SubscriptionDataMockService } from './subscription-data.mock.service';
import { ColumnRef, RuleMapperService } from './subscription-filter.adapter';

@Injectable()
export class ProjectSubscriptionAdapter {
	private readonly SubscriptionStatusMapping: Record<
		string | HistoryStatusModel,
		SubscriptionStatus
	> = {
		[HistoryStatusModel.UNPUBLISHED]: SubscriptionStatus.Unpublish,
		[HistoryStatusModel.AVAILABLE]: SubscriptionStatus.Available,
		[HistoryStatusModel.UNAVAILABLE]: SubscriptionStatus.UnAvailable,
		[HistoryStatusModel.SUSPENDED]: SubscriptionStatus.UnAvailable,
		[HistoryStatusModel.WAITING]: SubscriptionStatus.Waiting,
		[HistoryStatusModel.RUNNING]: SubscriptionStatus.Running,
		[HistoryStatusModel.SUCCESS]: SubscriptionStatus.Success,
		[HistoryStatusModel.ERROR]: SubscriptionStatus.Error,
		[HistoryStatusModel.FAILED]: SubscriptionStatus.Error,
		[HistoryStatusModel.UNKNOWN]: SubscriptionStatus.Unknown,
	};

	public constructor(
		private readonly projectStorage: ProjectStorage,
		private readonly dataStore: SubscriptionDataMockService,
		private readonly tagAdapter: TagAdapter,
		private readonly rulesMapper: RuleMapperService,
		@Inject(API_BASE_URL_BACKEND)
		private readonly apiBase: DcCoreBackendBasePath
	) {}

	public generateSubscriptions(
		response: ReadonlyArray<ISubscriptionResponse>
	): ImMap<string, SubscriptionItemEntity> {
		const tuples = response.reduce((acc, curr) => {
			const entity = this.generateSubscriptionItem(curr);
			acc.push([`${curr.id}`, entity]);
			return acc;
		}, new Array<[string, SubscriptionItemEntity]>());

		return ImMap(tuples);
	}

	public generateSubscriptionsAfterWS(
		list: ImMap<string, SubscriptionItemEntity>,
		updatedSubscription: ISubscriptionResponse
	): ImMap<string, SubscriptionItemEntity> {
		const elem = list.get(`${updatedSubscription.id}`);

		if (!elem) {
			return list;
		}
		const item = this.generateSubscriptionItem(updatedSubscription);
		return list.set(`${item.id}`, item.setPermissions(elem.permissions));
	}

	public generateShareableDatablockList(
		response: ReadonlyArray<IShareableDatablock>
	): ImMap<string, SubscriptionDatablockItemEntity> {
		const tuples = response.reduce((acc, curr) => {
			const entity = this.generateSubscriptionDatablockItemEntity(curr);
			acc.push([`${curr.id}`, entity]);
			return acc;
		}, new Array<[string, SubscriptionDatablockItemEntity]>());
		return ImMap(tuples);
	}

	public generateDatablockColumns(
		response: ReadonlyArray<IShareableDatablockColumn>
	): Array<DatablockColumn> {
		return response.map<DatablockColumn>((column) => ({
			id: column.uuid ?? '-1',
			label: column.lib ?? '',
			type: column.type ? this.getColumnType(column.type) : ColumnType.Unknown,
			isList: column.is_list ?? false,
			isHashed: false,
		}));
	}

	public generateAvailableColumns(
		response: ReadonlyArray<IShareableDatablockColumn>
	): ImMap<string, ColumnConfigItemEntity> {
		const tuples = response.reduce((acc, curr) => {
			const entity = this.generateSubscriptionColumnConfigEntity(curr);
			acc.push([entity.id, entity]);
			return acc;
		}, new Array<[string, ColumnConfigItemEntity]>());
		return OrderedMap(tuples);
	}

	public generateProjectList(): ImMap<string, SubscriptionProjectItemEntity> {
		const tuples = this.dataStore.generateProjects(10);

		return toImmutableMap(tuples, 'id');
	}

	public generateSubscriptionDetailsEntity(
		subscription: ISubscriptionResponse,
		columns: ReadonlyArray<ISubscriptionColumnConfig>,
		columnModels: ReadonlyArray<IColumnTemplate>,
		filterModels: ReadonlyArray<IFilterTemplate>,
		consumedProjects: ReadonlyArray<ISubscriptionProjectModel>,
		permissions: Record<ElementPermission, boolean>
	): ViewDetailsEntity {
		const subscriptionColumns = this.generateSubscriptionColumns(columns);

		const profiles = subscription.draft_configuration
			? this.generateSubscriptionProfiles(
					subscription.draft_configuration.profiles ?? [],
					subscriptionColumns
			  )
			: ImMap<string, ProfileItemEntity>();

		const subscriptionColumnModels = this.generateSubscriptionColumnModels(
			columnModels,
			subscriptionColumns,
			profiles
		);

		const subscriptionFilterModels = this.generateSubscriptionFilterModels(
			filterModels,
			profiles
		);

		const status = subscription.status
			? this.getSubscriptionStatus(subscription.status, subscription.history)
			: SubscriptionStatus.Unknown;

		const permissionList = permissions
			? Object.entries(permissions)
					.filter(([, value]) => value)
					.map(([permission]) => permission as ElementPermission)
			: [];

		return ViewDetailsEntity.build({
			id: subscription.id,
			published: this.generatePublishedSubscriptionEntity(
				subscription,
				subscriptionColumns.size
			),
			actionInProgress:
				status === SubscriptionStatus.Waiting ? 'in-progress' : 'none',
			internalMetadata: subscription.metadata
				? this.generateInternalMetadataEntity(subscription.metadata)
				: InternalMetadataEntity.build(),
			publicMetadata: subscription.public_metadata
				? this.generatePublicMetadataEntity(subscription.public_metadata)
				: PublicMetadataEntity.build(),
			permissions: ImSet(permissionList),
			availableColumns: subscriptionColumns,
			profiles,
			columnModels: subscriptionColumnModels,
			filterModels: subscriptionFilterModels,
			consumedProjects: this.generateConsumedProjects(consumedProjects),
		});
	}

	public getPublishedSubscription(
		publishedSubscription: PublishedSubscriptionEntity,
		subscription: ISubscriptionResponse
	): PublishedSubscriptionEntity {
		const isInSync =
			subscription.draft_configuration?.snapshot_diff === 'IN_SYNC' ?? false;
		return publishedSubscription.setIsInSync(isInSync);
	}

	public generateInternalMetadataEntity(
		response: IDcMetadataResponse
	): InternalMetadataEntity {
		const tags =
			response.tags?.map((t) =>
				AppTagItemEntity.build({
					id: t.id,
					label: t.code,
					color: t.color,
				})
			) ?? [];

		const createdByAvatar = response.created_by
			? AvatarEntity.build(response.created_by)
			: null;

		const updatedByAvatar = response.updated_by
			? AvatarEntity.build(response.updated_by)
			: null;

		return InternalMetadataEntity.build({
			id: response.id ?? -1,
			label: response.label,
			picId: response.icon_id ?? -1,
			description: response.description,
			tags: OrderedSet(tags),
			creationDate: response.creation_date
				? dayjs(response.creation_date)
				: null,
			updateDate: response.update_date ? dayjs(response.update_date) : null,
			createdBy: createdByAvatar,
			updatedBy: updatedByAvatar,
		});
	}

	public generatePublicMetadataEntity(
		response: IPublicMetadata
	): PublicMetadataEntity {
		const keywords =
			response.keywords?.map((keyword) =>
				AppTagItemEntity.buildKeyword({
					label: keyword,
				})
			) ?? [];

		const createdByAvatar = response.created_by
			? AvatarEntity.build(response.created_by)
			: null;

		const updatedByAvatar = response.updated_by
			? AvatarEntity.build(response.updated_by)
			: null;

		return PublicMetadataEntity.build({
			title: response.title,
			description: response.details,
			keywords: OrderedSet(keywords),
			creationDate: response.creation_date
				? dayjs(response.creation_date)
				: null,
			updateDate: response.update_date ? dayjs(response.update_date) : null,
			createdBy: createdByAvatar,
			updatedBy: updatedByAvatar,
		});
	}

	public generateSubscriptionProfiles(
		response: Array<ISubscriptionProfile>,
		subscriptionColumns: ImMap<string, ColumnConfigItemEntity>
	): ImMap<string, ProfileItemEntity> {
		const tuples = response.reduce((acc, curr) => {
			const filterModelIds = curr.filter_templates
				? curr.filter_templates
						.map((col) => col.uuid)
						.filter((uuid): uuid is string => uuid !== undefined)
				: Array<string>();

			const columnUuids = curr.column_template
				? ImSet(curr.column_template.columns.map((column) => column.uuid))
				: ImSet();

			const isDeleted = subscriptionColumns.some(
				(column) => columnUuids.has(column.id) && column.isDeleted
			);

			const entity = ProfileItemEntity.build({
				id: curr.uuid,
				label: curr.label,
				description: curr.description,
				columnModelId: curr.column_template ? curr.column_template.uuid : '',
				filterModelIds: ImSet(filterModelIds),
				status: isDeleted ? ColumnStatus.Warning : ColumnStatus.None,
				isInSync: curr.snapshot_diff === 'IN_SYNC' ?? false,
			});
			acc.push(entity);
			return acc;
		}, new Array<ProfileItemEntity>());

		return toImmutableMap(tuples, 'id');
	}

	public generateSubscriptionColumns(
		response: ReadonlyArray<ISubscriptionColumnConfig>
	): ImMap<string, ColumnConfigItemEntity> {
		const extractUsages = (
			usages: SubscriptionColumnUsage
		): ColumnReferences => {
			const {
				column_template_uuids: columnTemplateUuids,
				filter_template_uuids: filterTemplateUuids,
				profile_uuids: profileUuids,
			} = usages;
			const referrers: ColumnReferences = {
				total: 0,
				columnModels: [],
				filterModels: [],
				profiles: [],
			};
			if (columnTemplateUuids) {
				columnTemplateUuids.forEach((item) => {
					referrers.total++;
					referrers.columnModels.push({
						uuid: item.uuid,
						label: item.label,
					});
				});
			}
			if (filterTemplateUuids) {
				filterTemplateUuids.forEach((item) => {
					referrers.total++;
					referrers.filterModels.push({
						uuid: item.uuid,
						label: item.label,
					});
				});
			}
			if (profileUuids) {
				profileUuids.forEach((item) => {
					referrers.total++;

					referrers.profiles.push({
						uuid: item.uuid,
						label: item.label,
					});
				});
			}
			return referrers;
		};

		const tuples = response.reduce((acc, curr) => {
			const entity = ColumnConfigItemEntity.build({
				id: curr.uuid,
				label: curr.label,
				alias: curr.alias ?? curr.label,
				description: curr.description,
				isSelectedInSource: curr.active,
				columnType: curr.type
					? this.getColumnType(curr.type)
					: ColumnType.Unknown,
				isList: curr.list,
				isDeleted: curr.sourceDiff?.includes(SubscriptionColumnStatus.DELETED),
				referencedBy: curr.usages
					? extractUsages(curr.usages)
					: ({
							total: 0,
							profiles: [],
							filterModels: [],
							columnModels: [],
					  } as ColumnReferences),
			});
			acc.push([`${entity.id}`, entity]);
			return acc;
		}, new Array<[string, ColumnConfigItemEntity]>());

		return OrderedMap(tuples);
	}

	public generateSubscriptionColumnModels(
		response: ReadonlyArray<IColumnTemplate>,
		subscriptionColumns: ImMap<string, ColumnConfigItemEntity>,
		subscriptionProfiles: ImMap<string, ProfileItemEntity>
	): ImMap<string, ColumnModelItemEntity> {
		const tuples = response.reduce((acc, curr) => {
			const columns = curr.columns.reduce<ImSet<ColumnConfigItemEntity>>(
				(acc, curr) => {
					if (!curr.uuid) {
						return acc;
					}

					const col = subscriptionColumns.get(curr.uuid);

					if (!col) {
						return acc;
					}

					const temp = col.setIsActive(true).setIsHashed(curr.hash ?? false);
					return acc.add(temp);
				},
				ImSet()
			);

			const linkedProfiles = subscriptionProfiles.filter(
				(p) => p.columnModelId === curr.uuid
			);

			const isDeleted = columns.some((col) => col.isDeleted);

			const entity = ColumnModelItemEntity.build({
				id: curr.uuid,
				label: curr.label,
				description: curr.description,
				columns,
				linkedProfileIds: linkedProfiles.keySeq().toSet(),
				status: isDeleted ? ColumnStatus.Warning : ColumnStatus.None,
				isInSync: curr.snapshot_diff === 'IN_SYNC' ?? false,
			});
			acc.push(entity);
			return acc;
		}, new Array<ColumnModelItemEntity>());

		return toImmutableMap(tuples, 'id');
	}

	public generateSubscriptionFilterModels(
		iFilters: ReadonlyArray<IFilterTemplate>,
		iProfiles: ImMap<string, ProfileItemEntity>
	): ImMap<string, FilterModelItemEntity> {
		const iPro = iProfiles as ImMap<string, ProfileItemEntity>;
		const tuples = iFilters.reduce((acc, curr) => {
			if (curr.filter === undefined || curr.uuid === undefined) {
				return acc;
			}
			const rootGroup = this.rulesMapper.toFrontendGroup(
				curr.filter,
				new Map<string, ColumnRef>([])
			);

			const { uuid } = curr;

			const linkedProfiles = iPro.filter((p) => p.filterModelIds.has(uuid));

			const filter = FilterModelItemEntity.build({
				id: uuid,
				label: curr.label,
				description: curr.description,
				root: rootGroup,
				linkedProfileIds: linkedProfiles.keySeq().toSet(),
				isInSync: curr.snapshot_diff === 'IN_SYNC' ?? false,
			});
			acc.push(filter);
			return acc;
		}, new Array<FilterModelItemEntity>());
		return toImmutableMap(tuples, 'id');
	}

	public generatePublicationHistories(
		response: Array<ISubscriptionHistory>
	): ImMap<string, HistoryItemEntity> {
		const tuples = response
			.reduce((acc, curr) => {
				const entity = HistoryItemEntity.build({
					id: curr.id ?? -1,
					status: curr.status
						? this.SubscriptionStatusMapping[curr.status]
						: SubscriptionStatus.Unknown,
					action: curr.action ? this.getSubscriptionAction(curr.action) : '',
					columnCount: curr.column_count ?? 0,
					lineCount: curr.row_count ?? 0,
					date: curr.update_date ? dayjs(curr.update_date) : null,
					duration: curr.duration ? dayjs.duration(curr.duration) : null,
					avatar: curr.updated_by
						? AvatarEntity.build(
								`${curr.updated_by.first_name} ${curr.updated_by.last_name}`
						  )
						: null,
				});
				acc.push([`${entity.id}`, entity]);
				return acc;
			}, new Array<[string, HistoryItemEntity]>())
			.sort(
				([, a], [, b]) => (b.date?.valueOf() ?? 0) - (a.date?.valueOf() ?? 0)
			);
		return ImMap(tuples);
	}

	public generateConsumedProjects(
		response: ReadonlyArray<ISubscriptionProjectModel>
	): ImMap<string, ConsumedProjectItemEntity> {
		const tuples = response.reduce((acc, curr) => {
			const entity = ConsumedProjectItemEntity.build({
				id: curr.id,
				projectUuid: curr.target_project_uuid,
				projectLabel: curr.target_project_label,
				targetProjectVersions: curr.consumer_versions?.map(
					(v) => `V ${v.consumer_version}`
				),
				profileUuid: curr.data_link_profile_uuid,
				profileLabel: curr.data_link_profile_label,
				columnProfileCount: curr.column_profile_count ?? 0,
				sourceProjectVersion: curr.producer_version
					? `V ${curr.producer_version}`
					: '',
				active: curr.active,
				status: curr.status
					? this.getConsumedProjectStatus(curr.status)
					: ConsumeStatus.Unknown,
				startDate: curr.data_link_start_date
					? dayjs(curr.data_link_start_date)
					: null,
				isInSync: curr.snapshot_diff === 'IN_SYNC' ?? false,
				isInCurrentVersion: curr.reads_this_version ?? false,
			});
			acc.push(entity);
			return acc;
		}, new Array<ConsumedProjectItemEntity>());

		return toImmutableMap(tuples, 'id');
	}

	public generateProfileColumns(
		response: ReadonlyArray<IConsumedProfileColumn>
	): Array<DatablockColumn> {
		return response.map<DatablockColumn>((column) => ({
			id: column.uuid ?? '-1',
			label: column.alias ?? '',
			type: column.type ? this.getColumnType(column.type) : ColumnType.Unknown,
			isList: column.list ?? false,
			isHashed: column.hashed ?? false,
		}));
	}

	public generateSwitchableConsumedProjects(
		response: ReadonlyArray<ISwitchableProjectModel>
	): ImMap<string, SwitchableVersionProjectItemEntity> {
		const tuples = response.reduce((acc, curr) => {
			const entity = SwitchableVersionProjectItemEntity.build({
				id: curr.connection_id,
				subscriptionLabel: curr.data_link_label ?? '',
				consumerProjectId: curr.consumer_project_id ?? -1,
				consumerProjectLabel: curr.consumer_project_label ?? '',
				consumerProjectVersion: curr.consumer_project_version
					? `V ${curr.consumer_project_version}`
					: '',
				profileLabel: curr.data_link_profile_label ?? '',
				currentProducerProjectId: curr.current_producer_project_id ?? -1,
				currentProducerVersion: curr.current_producer_version
					? `V ${curr.current_producer_version}`
					: '',
				switchable: curr.switchable ?? false,
				blockedReason: curr.blocked_reason
					? this.getVersionSwitchBlockedReason(curr.blocked_reason)
					: VersionSwitchBlockedReason.None,
			});
			acc.push(entity);
			return acc;
		}, new Array<SwitchableVersionProjectItemEntity>());

		return toImmutableMap(tuples, 'id');
	}

	public generateProjects(
		response: ReadonlyArray<IProjectModel>
	): ImMap<string, ProjectItemEntity> {
		const tuples = response.reduce((acc, curr) => {
			const entity = ProjectItemEntity.build({
				id: curr.id,
				uuid: curr.uuid ?? '-1',
				label: curr.metadata?.label ?? '',
				description: curr.metadata?.description ?? '',
				version: curr.version ? `V ${curr.version}` : '',
				tags: this.tagAdapter.generateResponseMetadataTag(
					curr.metadata?.tags ?? []
				),
			});
			acc.push(entity);
			return acc;
		}, new Array<ProjectItemEntity>());

		return toImmutableMap(tuples, 'id');
	}

	public generateSimulationData(
		response: ParsedSimulationResult
	): SimulationResultEntity {
		return SimulationResultEntity.build({
			data: response.result,
			count: response.count,
		});
	}

	public generateMembersAccess(
		response: Array<ISubscriptionMemberAccess>
	): ImMap<string, MemberAccessEntity> {
		return response.reduce((acc, item) => {
			const permissions = Object.entries(item.permissions).reduce<
				ImSet<string>
			>((set, [key, value]) => {
				if (value) {
					return set.add(key);
				}
				return set;
			}, ImSet<string>());

			const access = MemberAccessEntity.build({
				id: item.user_id,
				memberUuid: item.user_name ?? '-1',
				isGroup: item.is_group ?? false,
				permissions,
			});

			return acc.set(access.id, access);
		}, ImMap<string, MemberAccessEntity>());
	}

	public getSubscriptionStatus(
		status: HistoryStatusModel,
		history?: Array<ISubscriptionHistory>
	): SubscriptionStatus {
		if (
			status === HistoryStatusModel.UNPUBLISHED &&
			history &&
			history.length === 0
		) {
			return this.SubscriptionStatusMapping[status];
		} else if (
			status === HistoryStatusModel.UNPUBLISHED &&
			history &&
			history.length !== 0
		) {
			const sortedHistory = history.sort((a, b) => {
				const dateA = Number(a.update_date) || 0;
				const dateB = Number(b.update_date) || 0;
				return dateB - dateA;
			});
			const latestKnownStatus =
				sortedHistory[0].status ?? HistoryStatusModel.UNPUBLISHED;
			return this.SubscriptionStatusMapping[latestKnownStatus];
		} else if (
			status === HistoryStatusModel.AVAILABLE &&
			history &&
			history.length !== 0
		) {
			const sortedHistory = history.sort((a, b) => {
				const dateA = Number(a.update_date) || 0;
				const dateB = Number(b.update_date) || 0;
				return dateB - dateA;
			});
			const latestKnownStatus =
				sortedHistory[0].status !== HistoryStatusModel.WAITING &&
				sortedHistory[0].status !== HistoryStatusModel.ERROR
					? status
					: sortedHistory[0].status;
			return this.SubscriptionStatusMapping[latestKnownStatus];
		}
		return this.SubscriptionStatusMapping[status];
	}

	private generatePublishedSubscriptionEntity(
		response: ISubscriptionResponse,
		columnCount: number
	): PublishedSubscriptionEntity {
		const keywords =
			response.public_metadata?.keywords?.map((keyword) =>
				AppTagItemEntity.buildKeyword({
					label: keyword,
				})
			) ?? [];

		const status = response.status
			? this.getSubscriptionStatus(response.status, response.history)
			: SubscriptionStatus.Unknown;

		const initializedByAvatar = response.history
			? response.history.length !== 0 && response.history[0].updated_by
				? AvatarEntity.build(`${response.history[0].updated_by.name}`)
				: null
			: null;

		return PublishedSubscriptionEntity.build({
			id: response.id,
			title: response.public_metadata?.title,
			details: response.public_metadata?.details,
			keywords,
			datablockId: response.datablock?.id ?? -1,
			datablockLabel: response.datablock?.label ?? '',
			datablockUrl: response.datablock?.id
				? this.parseDatablockPageUrl(response.datablock.id)
				: '',
			status,
			publishedDate: response.last_activation_date
				? dayjs(response.last_activation_date)
				: null,
			publishedBy: initializedByAvatar,
			dataCount: response.row_count ?? 0,
			columnCount: response.nbr_of_cols ?? columnCount,
			history: response.history?.map<SubscriptionHistory>((h) => ({
				id: h.id ?? -1,
				status: h.status
					? this.SubscriptionStatusMapping[h.status]
					: SubscriptionStatus.Unknown,
				action: h.action ? this.getSubscriptionAction(h.action) : '',
				duration: h.duration ? dayjs.duration(h.duration) : null,
				columnCount: h.column_count ?? 0,
				lineCount: h.row_count ?? 0,
				date: h.update_date ? dayjs(h.update_date) : null,
				avatar: h.updated_by
					? AvatarEntity.build(
							`${h.updated_by.first_name} ${h.updated_by.last_name}`
					  )
					: null,
			})),
			isInSync:
				response.draft_configuration?.snapshot_diff === 'IN_SYNC' ?? false,
		});
	}

	private generateSubscriptionItem(
		response: ISubscriptionResponse
	): SubscriptionItemEntity {
		const tags =
			response.metadata?.tags?.map((t) =>
				AppTagItemEntity.build({
					id: t.id,
					label: t.code,
					color: t.color,
				})
			) ?? [];

		const updatedByAvatar = response.last_updated_by
			? AvatarEntity.build(
					`${response.last_updated_by.last_name} ${response.last_updated_by.first_name}`
			  )
			: null;

		const status = response.status
			? this.getSubscriptionStatus(response.status, response.history)
			: SubscriptionStatus.Unknown;

		const permissionList = response.permissions
			? Object.entries(response.permissions)
					.filter(([, value]) => value)
					.map(([permission]) => permission as ElementPermission)
			: [];

		const resourceUrl =
			DcBaseInitializationService.getResource('PICTOGRAMME_IMAGE');
		return SubscriptionItemEntity.build({
			id: response.id,
			label: response.metadata?.label ?? '',
			pictogramUrl: `${this.apiBase.getBasePath()}/${resourceUrl}?id=${
				response.metadata?.icon_id ?? -1
			}`,
			tags: OrderedSet(tags),
			datablockId: response.datablock?.id ?? -1,
			datablockSource: response.datablock?.label ?? '',
			sourceUrl: response.datablock?.id
				? this.parseDatablockPageUrl(response.datablock.id)
				: '',
			status,
			actionInProgress:
				status === SubscriptionStatus.Waiting ? 'in-progress' : 'none',
			dataCount: response.row_count,
			updatedBy: updatedByAvatar,
			permissions: ImSet(permissionList),
		});
	}

	private generateSubscriptionColumnConfigEntity(
		response: IShareableDatablockColumn
	): ColumnConfigItemEntity {
		return ColumnConfigItemEntity.build({
			id: response.uuid,
			label: response.lib,
			columnType: response.type
				? this.getColumnType(response.type)
				: ColumnType.Unknown,
			alias: response.lib ? normalize(response.lib) : '',
			isList: response.is_list,
		});
	}

	private generateSubscriptionDatablockItemEntity(
		response: IShareableDatablock
	): SubscriptionDatablockItemEntity {
		return SubscriptionDatablockItemEntity.build({
			id: response.id,
			label: response.label,
			url: response.id ? this.parseDatablockPageUrl(response.id) : '',
			updateDate: response.last_update ? dayjs(response.last_update) : null,
			updatedBy: response.last_update_by
				? `${response.last_update_by.first_name} ${response.last_update_by.last_name}`
				: '',
			columnsCount: response.column_count,
		});
	}

	private parseDatablockPageUrl(datablockId: number): string {
		const projectId = this.projectStorage.getValue()?.projectId;
		return DcRouteUrlBuilder.buildEditPageUrl(
			projectId,
			datablockId,
			'DATABLOCK'
		);
	}

	private getColumnType(type: string): ColumnType {
		switch (type.toLowerCase()) {
			case 'string':
				return ColumnType.Str;
			case 'file':
				return ColumnType.File;
			case 'double':
				return ColumnType.Double;
			case 'decimal':
				return ColumnType.Dec;
			case 'date':
				return ColumnType.Date;
			case 'biginteger':
				return ColumnType.BigInt;
			case 'big_integer':
				return ColumnType.BigInt;
			case 'binary':
				return ColumnType.Bin;
			case 'words':
				return ColumnType.Words;
			case 'integer':
				return ColumnType.Int;
			case 'boolean':
				return ColumnType.Bool;
			case 'geometry':
				return ColumnType.Geo;
			default:
				return ColumnType.Unknown;
		}
	}

	private getSubscriptionAction(action: HistoryAction): string {
		switch (action) {
			case HistoryAction.CREATE_SNAPSHOT:
				return $localize`:i18n=@@subscription.publication.actionType.publish:`;
			case HistoryAction.UPDATE_SNAPSHOT:
				return $localize`:i18n=@@subscription.publication.actionType.updateData:`;
			case HistoryAction.UPDATE_CONF:
				return $localize`:i18n=@@subscription.publication.actionType.updateConfig:`;
			case HistoryAction.UPDATE_TEMPLATES:
				return $localize`:i18n=@@subscription.publication.actionType.updateTemplate:`;
			case HistoryAction.UPDATE_PUBLIC_METADATA:
				return $localize`:i18n=@@subscription.publication.actionType.updatePublicMetadata:`;
			case HistoryAction.SUSPEND:
				return $localize`:i18n=@@subscription.publication.actionType.suspend:`;
			case HistoryAction.REACTIVATE:
				return $localize`:i18n=@@subscription.publication.actionType.reactivate:`;
			case HistoryAction.CLEAR_DATA:
				return $localize`:i18n=@@subscription.publication.actionType.clearData:`;
			default:
				return $localize`:i18n=@@subscription.publication.actionType.unknown:`;
		}
	}

	private getConsumedProjectStatus(
		status: ProjectSubscriptionStatus
	): ConsumeStatus {
		switch (status) {
			case 'IN_PROGRESS':
				return ConsumeStatus.InProgress;
			case 'PAUSED':
				return ConsumeStatus.Paused;
			case 'ABSENT':
				return ConsumeStatus.Absent;
			case 'NOT_USED':
				return ConsumeStatus.NotUsed;
			default:
				return ConsumeStatus.Unknown;
		}
	}

	private getVersionSwitchBlockedReason(
		reason: BlockedReason
	): VersionSwitchBlockedReason {
		switch (reason) {
			case BlockedReason.DCREPOSITORY_REFUSED:
				return VersionSwitchBlockedReason.RepositoryRefused;
			case BlockedReason.NOT_IN_OFFER:
				return VersionSwitchBlockedReason.NotInOffer;
			case BlockedReason.VERSION_NOT_PUBLISHED:
				return VersionSwitchBlockedReason.VersionNotPublished;
			case BlockedReason.PROFILE_INCOMPATIBLE:
				return VersionSwitchBlockedReason.ProfileIncompatible;
			case BlockedReason.PROFILE_GONE:
				return VersionSwitchBlockedReason.ProfileGone;
			case BlockedReason.ACCESS_GONE:
				return VersionSwitchBlockedReason.AccessGone;
			case BlockedReason.AUTHORIZATION_PAUSED:
				return VersionSwitchBlockedReason.Authorization_paused;
			default:
				return VersionSwitchBlockedReason.None;
		}
	}
}
